/**
 * V111: MM 현황 데이터 조회 Hook
 * V130.3: 프리패치 캐시 추가 (이전/다음 달 선로딩)
 * V131: SWR 패턴 + sessionStorage 캐시 + 성능 계측
 *
 * getMmOverviewData Cloud Function을 호출하여
 * 팀별 MM 현황 데이터를 조회합니다.
 *
 * @module shared/hooks/useMmOverviewData
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';
import {
  GetMmOverviewDataRequest,
  GetMmOverviewDataResponse,
  MmTeamSummary,
  DebugPerfInfo,
  getCurrentMonth,
  addMonths,
} from '../types/mm';

// ============================================
// V131: 성능 계측 유틸리티
// ============================================

/** V131: 성능 로그 출력 (no-op, logging removed) */
function logPerf(
  _month: string,
  _companyCode: string,
  _cache: 'HIT' | 'MISS' | 'SESSION' | 'STALE',
  _netMs?: number,
  _totalMs?: number
) {
  // Performance logging removed
}

// ============================================
// V130.3 + V131: 프리패치 캐시 (모듈 레벨)
// ============================================

/**
 * V130.3: 캐시 데이터 타입
 */
interface CachedMmData {
  teams: MmTeamSummary[];
  workingDays: number;
  holidays: Array<{ date: string; name: string }>;
  timestamp: number; // 캐시 시간 (TTL 관리용)
}

/**
 * V130.3: 프리패치 캐시 (in-memory)
 * - Key: 'YYYY-MM_companyCode' (예: '2026-02_KR')
 * - 캐시 TTL: 5분 (300000ms)
 */
const mmDataCache = new Map<string, CachedMmData>();

/**
 * V130.3: 진행 중인 요청 추적 (중복 요청 방지)
 * - Key: 'YYYY-MM_companyCode'
 * - Value: Promise<CachedMmData | null>
 */
const pendingRequests = new Map<string, Promise<CachedMmData | null>>();

/** V130.3: 캐시 TTL (5분) */
const CACHE_TTL_MS = 5 * 60 * 1000;

/** V131: sessionStorage 키 prefix */
const SESSION_STORAGE_PREFIX = 'mmOverview_';

/**
 * V130.3: 캐시 키 생성
 */
function getCacheKey(month: string, companyCode: string, viewMode?: string): string {
  return viewMode ? `${month}_${companyCode}_${viewMode}` : `${month}_${companyCode}`;
}

/**
 * V130.3: 캐시 유효성 확인
 */
function isCacheValid(cached: CachedMmData | undefined): boolean {
  if (!cached) return false;
  return Date.now() - cached.timestamp < CACHE_TTL_MS;
}

/**
 * V130.3: 메모리 캐시에서 데이터 조회
 */
function getFromMemoryCache(month: string, companyCode: string, viewMode?: string): CachedMmData | null {
  const key = getCacheKey(month, companyCode, viewMode);
  const cached = mmDataCache.get(key);
  if (isCacheValid(cached)) {
    return cached!;
  }
  return null;
}

/**
 * V131: sessionStorage에서 데이터 조회
 * - 안전하게 try/catch로 감쌈 (파싱 실패 시 null)
 */
function getFromSessionStorage(month: string, companyCode: string, viewMode?: string): CachedMmData | null {
  try {
    const key = SESSION_STORAGE_PREFIX + getCacheKey(month, companyCode, viewMode);
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as CachedMmData;
    if (isCacheValid(parsed)) {
      return parsed;
    }
    // 만료된 데이터는 삭제
    sessionStorage.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

/**
 * V131: sessionStorage에 데이터 저장
 * - 안전하게 try/catch로 감쌈 (저장 실패 시 무시)
 */
function setToSessionStorage(month: string, companyCode: string, data: CachedMmData, viewMode?: string): void {
  try {
    const key = SESSION_STORAGE_PREFIX + getCacheKey(month, companyCode, viewMode);
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    // 저장 실패 (quota 초과 등) - 무시
  }
}

/**
 * V130.3 + V131: 캐시에서 데이터 조회 (메모리 → sessionStorage 순서)
 * @returns { data, source } - source: 'memory' | 'session' | null
 */
function getFromCache(month: string, companyCode: string, viewMode?: string): { data: CachedMmData | null; source: 'memory' | 'session' | null } {
  // 1. 메모리 캐시 먼저
  const memCached = getFromMemoryCache(month, companyCode, viewMode);
  if (memCached) {
    return { data: memCached, source: 'memory' };
  }

  // 2. sessionStorage
  const sessionCached = getFromSessionStorage(month, companyCode, viewMode);
  if (sessionCached) {
    // sessionStorage hit → 메모리에도 복사
    const key = getCacheKey(month, companyCode, viewMode);
    mmDataCache.set(key, sessionCached);
    return { data: sessionCached, source: 'session' };
  }

  return { data: null, source: null };
}

/**
 * V130.3 + V131: 캐시에 데이터 저장 (메모리 + sessionStorage)
 */
function setToCache(month: string, companyCode: string, data: Omit<CachedMmData, 'timestamp'>, viewMode?: string): CachedMmData {
  const key = getCacheKey(month, companyCode, viewMode);
  const cachedData: CachedMmData = {
    ...data,
    timestamp: Date.now(),
  };

  // 메모리 캐시
  mmDataCache.set(key, cachedData);

  // sessionStorage
  setToSessionStorage(month, companyCode, cachedData, viewMode);

  return cachedData;
}

/**
 * V131.3: BE 성능 정보 로그 출력
 */
function logBePerfInfo(_month: string, _debugPerf: DebugPerfInfo): void {
  // Performance logging removed
}

/**
 * V130.3 + V131: 단일 월 데이터 fetch (캐시/중복 요청 처리 포함)
 * V132: skipCache 파라미터 추가 (refetch 시 캐시 우회)
 * @param debugPerf V131.3: true면 BE 성능 정보 요청 (adminLike 필요)
 * @param skipCache V132: true면 캐시 확인 없이 강제 fetch (refetch용)
 * @returns { data, netMs, bePerf } - 네트워크 요청 시간 및 BE 성능 정보 포함
 */
async function fetchMonthData(
  month: string,
  companyCode: 'KR' | 'CN',
  debugPerf?: boolean,
  skipCache?: boolean,
  viewMode?: 'overview' | 'register'
): Promise<{
  data: CachedMmData | null;
  netMs?: number;
  resolvedCompanyCode?: 'KR' | 'CN';
  debugInfo?: GetMmOverviewDataResponse['debugInfo'];
}> {
  const key = getCacheKey(month, companyCode, viewMode);

  // V132.9: skipCache=true면 캐시와 pending 요청 모두 무시 (refetch용)
  // - 저장 후 refetch 시 반드시 새 BE 호출 필요
  if (!skipCache) {
    // 1. 캐시 확인
    const cached = getFromCache(month, companyCode, viewMode);
    if (cached.data) {
      logPerf(month, companyCode, cached.source === 'memory' ? 'HIT' : 'SESSION', undefined, 0);
      return { data: cached.data };
    }

    // 2. 진행 중인 요청 확인 (중복 방지)
    const pending = pendingRequests.get(key);
    if (pending) {
      const result = await pending;
      return { data: result };
    }
  }

  // 3. 새 요청 시작
  const netStart = performance.now();
  // V233.1: BE 응답 캡처용
  let capturedDebugInfo: GetMmOverviewDataResponse['debugInfo'] = undefined;
  let capturedResolvedCC: 'KR' | 'CN' | undefined = undefined;

  const requestPromise = (async (): Promise<CachedMmData | null> => {
    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<GetMmOverviewDataRequest, GetMmOverviewDataResponse>(
        functions,
        'getMmOverviewData'
      );

      // V132.13: skipCache=true면 BE 서버 캐시도 우회 (skipServerCache)
      // V73.3: viewMode 전달 (MM현황 vs MM작성 스코프 분리)
      const result = await fn({
        month,
        companyCode,
        debugPerf,
        skipServerCache: skipCache,
        viewMode,
      });

      // V233.1: BE resolvedCompanyCode 캡처
      capturedResolvedCC = result.data.resolvedCompanyCode;

      // V131.3: BE 성능 정보 로그 출력
      if (result.data.debugPerf) {
        logBePerfInfo(month, result.data.debugPerf);
      }

      // V233.1: debugInfo 캡처
      if (result.data.debugInfo) {
        capturedDebugInfo = result.data.debugInfo;
      }

      if (result.data.success) {
        const cachedData = {
          teams: result.data.teams || [],
          workingDays: result.data.monthInfo?.workingDays || 0,
          holidays: result.data.monthInfo?.holidays || [],
        };

        // V233.1: BE가 교정한 companyCode가 다르면 교정 키로도 캐시 저장
        const resolvedCC = result.data.resolvedCompanyCode;
        if (resolvedCC && resolvedCC !== companyCode) {
          setToCache(month, resolvedCC, cachedData, viewMode);
        }

        const data = setToCache(month, companyCode, cachedData, viewMode);
        return data;
      } else {
        throw new Error(result.data.error || 'MM 현황 조회 실패');
      }
    } catch (err) {
      console.error(`[V131] Fetch error: ${key}`, err);
      return null;
    } finally {
      // 요청 완료 후 pending에서 제거
      pendingRequests.delete(key);
    }
  })();

  // 진행 중인 요청으로 등록
  pendingRequests.set(key, requestPromise);

  const result = await requestPromise;
  const netMs = Math.round(performance.now() - netStart);

  return { data: result, netMs, resolvedCompanyCode: capturedResolvedCC, debugInfo: capturedDebugInfo };
}

/**
 * V130.3: 프리패치 함수 (이전/다음 달 선로딩)
 * V131: 절대 UI를 block하지 않게 fire-and-forget
 * - 조용히 실패 (에러 토스트 없음, 로그만)
 * - 현재 월 로딩 성공 후 호출됨
 */
function prefetchAdjacentMonths(
  currentMonth: string,
  companyCode: 'KR' | 'CN',
  viewMode?: 'overview' | 'register'
): void {
  // V131: fire-and-forget - setTimeout(0)으로 렌더 이후에 실행
  setTimeout(() => {
    const prevMonth = addMonths(currentMonth, -1);
    const nextMonth = addMonths(currentMonth, 1);

    // 병렬로 프리패치 (실패해도 무시, await 없음)
    Promise.allSettled([
      fetchMonthData(prevMonth, companyCode, false, false, viewMode),
      fetchMonthData(nextMonth, companyCode, false, false, viewMode),
    ]).catch(() => {
      // 완전 무시
    });
  }, 0);
}

// ============================================
// Hook 정의
// ============================================

/**
 * MM 현황 데이터 조회 훅 옵션
 */
export interface UseMmOverviewDataOptions {
  /** 대상 월 (YYYY-MM) */
  month: string;
  /** 회사 코드 (KR/CN) */
  companyCode?: 'KR' | 'CN';
  /**
   * V131.3: 성능 디버그 정보 요청
   *
   * true로 설정하면 BE 성능 계측 정보가 콘솔에 출력됩니다.
   * - 권한: adminLike 권한 필요 (없으면 무시됨)
   * - 캐시: debugPerf=true일 때 서버 캐시 bypass (정확한 측정용)
   *
   * 사용법: useMmOverviewData({ month, debugPerf: true })
   */
  debugPerf?: boolean;
  /**
   * V73.3: 호출 페이지 구분 (MM현황 vs MM작성)
   * - 'overview': MM현황 → KR role_manager 스코프 ALL 승격
   * - 'register': MM작성 → KR role_manager 스코프 SUBTREE 유지
   */
  viewMode?: 'overview' | 'register';
}

/**
 * MM 현황 데이터 조회 훅 반환 타입
 */
export interface UseMmOverviewDataResult {
  /** 응답 데이터 (페이지에서 data로 접근용) */
  data: {
    teams: MmTeamSummary[];
    monthInfo: {
      workingDays: number;
      holidays: Array<{ date: string; name: string }>;
    };
    /** V233: 팀 0건 진단 정보 */
    debugInfo?: GetMmOverviewDataResponse['debugInfo'];
  } | null;

  /** V285: BE가 확정한 MM 기준 지사 companyCode */
  resolvedCompanyCode?: 'KR' | 'CN';

  /** 팀별 MM 데이터 (하위호환) */
  teams: MmTeamSummary[];

  /** 해당월 근무일수 (하위호환) */
  workingDays: number;

  /** 해당월 공휴일 목록 (하위호환) */
  holidays: Array<{ date: string; name: string }>;

  /** 로딩 상태 */
  isLoading: boolean;

  /** V131: 백그라운드 갱신 중 (SWR) */
  isRevalidating: boolean;

  /** 에러 */
  error: Error | null;

  /** 다시 조회 */
  refetch: () => Promise<void>;
}

/**
 * MM 현황 데이터 조회 Hook
 *
 * V130.3: 프리패치 캐시 추가
 * V131: SWR (Stale-While-Revalidate) 패턴
 * - 캐시 히트 시 즉시 표시 + 백그라운드 갱신
 * - sessionStorage로 새로고침 후에도 즉시 표시
 * - 중복 요청 방지 (동일 월 요청 진행 중이면 대기)
 *
 * @param month 대상 월 (YYYY-MM), 기본값: 현재 월
 * @returns MM 데이터 및 상태
 *
 * @example
 * ```typescript
 * function MMOverviewPage() {
 *   const [month, setMonth] = useState(getCurrentMonth());
 *   const { data, isLoading, error, refetch } = useMmOverviewData({ month, companyCode: 'KR' });
 *
 *   return (
 *     <div>
 *       {data?.teams.map(team => (
 *         <TeamSection key={team.teamId} team={team} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMmOverviewData(options: UseMmOverviewDataOptions): UseMmOverviewDataResult {
  const { user } = useCurrentUser();

  // V233.1: companyCode는 BE가 확정 (resolvedCompanyCode).
  // FE는 profile 값 또는 BE 이전 응답값을 힌트로 전달. 역할 기반 fallback 제거.
  const resolvedCCRef = useRef<'KR' | 'CN' | undefined>(undefined);

  const companyCode: 'KR' | 'CN' = (() => {
    if (options.companyCode) return options.companyCode;
    const profileCC = (user?.profile as any)?.companyCode;
    if (profileCC === 'KR' || profileCC === 'CN') return profileCC;
    if (resolvedCCRef.current) return resolvedCCRef.current;
    return 'KR'; // 초회 요청 시 BE가 교정
  })();

  const targetMonth = options.month || getCurrentMonth();
  // V131.3: 성능 디버그 정보 요청 여부
  const debugPerf = options.debugPerf ?? false;
  // V73.3: 호출 페이지 구분
  const viewMode = options.viewMode;

  const [teams, setTeams] = useState<MmTeamSummary[]>([]);
  const [workingDays, setWorkingDays] = useState(0);
  const [holidays, setHolidays] = useState<Array<{ date: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // V233: BE debugInfo (teams=0 진단용)
  const [debugInfo, setDebugInfo] = useState<GetMmOverviewDataResponse['debugInfo']>(undefined);

  // V130.3: 마운트 상태 추적 (메모리 누수 방지)
  const isMountedRef = useRef(true);
  // V131: 현재 로드 중인 key 추적 (month 변경 시 중복 방지)
  const currentKeyRef = useRef<string>('');
  // V132.10: 요청 ID 추적 (race condition 방지 - 최신 요청만 반영)
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async (forceRefresh = false) => {
    // V132.10: 새 요청 시작 시 requestId 증가
    const thisRequestId = ++requestIdRef.current;
    const totalStart = performance.now();
    const key = getCacheKey(targetMonth, companyCode, viewMode);
    currentKeyRef.current = key;

    try {
      // V131: SWR - 캐시 확인 (stale 데이터 즉시 표시)
      const cached = getFromCache(targetMonth, companyCode, viewMode);

      if (cached.data && !forceRefresh) {
        // 캐시 히트 - 즉시 표시
        setTeams(cached.data.teams);
        setWorkingDays(cached.data.workingDays);
        setHolidays(cached.data.holidays);
        setError(null);
        setIsLoading(false);

        logPerf(targetMonth, companyCode, cached.source === 'memory' ? 'HIT' : 'SESSION', undefined,
          Math.round(performance.now() - totalStart));

        // V131: SWR - 백그라운드에서 최신 데이터 fetch (stale-while-revalidate)
        // V292: 캐시 나이 5초 이상이면 백그라운드 갱신 (기존 2분 → 5초)
        // — OT 취소 승인 후 MM 페이지 재진입 시 즉시 반영되도록 SWR 임계값 단축
        const cacheAge = Date.now() - cached.data.timestamp;
        const REVALIDATE_THRESHOLD = 5 * 1000; // V292: 5초 (기존 2분)

        if (cacheAge > REVALIDATE_THRESHOLD) {
          setIsRevalidating(true);
          // 백그라운드 갱신 (fire-and-forget 스타일이지만 상태는 업데이트)
          // V132: SWR 갱신 시에도 캐시 우회 (skipCache=true)
          fetchMonthData(targetMonth, companyCode, false, true, viewMode).then(({ data: newData }) => {
            if (!isMountedRef.current || currentKeyRef.current !== key) return;

            if (newData) {
              setTeams(newData.teams);
              setWorkingDays(newData.workingDays);
              setHolidays(newData.holidays);
            }
            setIsRevalidating(false);
          }).catch(() => {
            if (isMountedRef.current) setIsRevalidating(false);
          });
        }

        // 프리패치는 수행
        prefetchAdjacentMonths(targetMonth, companyCode, viewMode);
        return;
      }

      // 캐시 미스 - 로딩 표시 후 fetch
      setIsLoading(true);
      setError(null);

      // V131.3: debugPerf=true이면 BE 성능 정보도 요청
      // V132: forceRefresh=true이면 캐시 우회 (skipCache=true)
      const { data: result, netMs, resolvedCompanyCode, debugInfo: fetchedDebugInfo } = await fetchMonthData(targetMonth, companyCode, debugPerf, forceRefresh, viewMode);

      // V132.10: 마운트 해제, 다른 key, 또는 더 새로운 요청이 있으면 무시
      if (!isMountedRef.current || currentKeyRef.current !== key || requestIdRef.current !== thisRequestId) {
        return;
      }

      // V233.1: BE resolvedCompanyCode를 ref에 저장 (다음 렌더 시 캐시 키에 활용)
      if (resolvedCompanyCode) {
        resolvedCCRef.current = resolvedCompanyCode;
      }

      // V233: debugInfo 상태 업데이트
      setDebugInfo(fetchedDebugInfo);

      if (result) {
        setTeams(result.teams);
        setWorkingDays(result.workingDays);
        setHolidays(result.holidays);

        const totalMs = Math.round(performance.now() - totalStart);
        logPerf(targetMonth, companyCode, 'MISS', netMs, totalMs);
        // 로딩 성공 후 이전/다음 달 프리패치
        prefetchAdjacentMonths(targetMonth, companyCode, viewMode);
      } else {
        throw new Error('MM 현황 조회 실패');
      }
    } catch (err: any) {
      // V132.10: stale 요청이면 무시
      if (!isMountedRef.current || currentKeyRef.current !== key || requestIdRef.current !== thisRequestId) return;
      console.error('[V131] useMmOverviewData error:', err);
      setError(err);
      setTeams([]);
      setWorkingDays(0);
      setHolidays([]);
    } finally {
      // V132.10: stale 요청이면 로딩 상태 변경하지 않음
      if (isMountedRef.current && currentKeyRef.current === key && requestIdRef.current === thisRequestId) {
        setIsLoading(false);
      }
    }
  }, [targetMonth, companyCode, debugPerf, viewMode]);

  // V73.4: 레거시 캐시 정리 (pre-V73.3 viewMode 없는 엔트리 제거)
  // — V73.3 이전에는 캐시 키에 viewMode가 없어 MM현황/MM등록 교차 오염 발생
  // — 배포 후 브라우저에 남은 old-format 엔트리를 일회성 정리
  useEffect(() => {
    try {
      // 메모리 캐시: viewMode suffix 없는 엔트리 제거
      for (const key of Array.from(mmDataCache.keys())) {
        if (!key.includes('_overview') && !key.includes('_register')) {
          mmDataCache.delete(key);
        }
      }
      // sessionStorage: viewMode suffix 없는 mmOverview_ 엔트리 제거
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const ssKey = sessionStorage.key(i);
        if (
          ssKey &&
          ssKey.startsWith(SESSION_STORAGE_PREFIX) &&
          !ssKey.includes('_overview') &&
          !ssKey.includes('_register')
        ) {
          sessionStorage.removeItem(ssKey);
        }
      }
    } catch {
      // 정리 실패 무시
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  // data 객체 생성 (null if loading or error)
  const data = isLoading || error
    ? null
    : {
        teams,
        monthInfo: {
          workingDays,
          holidays,
        },
        debugInfo, // V233: 팀 0건 진단 정보
      };

  return {
    data,
    resolvedCompanyCode: resolvedCCRef.current,
    teams,
    workingDays,
    holidays,
    isLoading,
    isRevalidating,
    error,
    refetch: () => fetchData(true),
  };
}
